// src/games/HuntWords/index.tsx
import { message } from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IoTriangleSharp } from 'react-icons/io5';
import { useNavigate, useParams } from 'react-router-dom';

import { GameHeader } from '../../components/GameHeader';
import { Sidebar } from '../../components/Sidebar';
import { useAuth } from '../../hooks/auth';
import { usePopup } from '../../hooks/popup';
import { FinishModal } from '../core/finish/FinishModal';
import type { FinishMemo } from '../core/finish/finishMemo';
import { useGameSession } from '../core/session/useGameSession';
import { WordSearchLoad, finishWordSearch, loadWordSearchGame, saveWordSearchProgress, wordSearchResult } from './api';
import Clue from './Clue';
import Game from './Game';
import type { ISolution } from './huntWordInterface';
import { WordSearchCache, addFound, isWordSearchComplete, remainingSolutions, wordsearchPoints } from './rules';
import { Container, Content, GameContainer, GameContent, Layout } from './styles';

const EMPTY: WordSearchCache = { found: [], correctCells: [] };

/**
 * Rota `/jogo/cacapalavras/:id`. O `key` remonta a tela (e a sessão) quando o id muda com a
 * rota montada: sem isso, as palavras do jogo A seriam salvas e finalizadas no jogo B. A tela
 * recebe o id por prop, nunca do `useParams`.
 */
const HuntWords: React.FC = () => {
  const jogoId = Number(useParams().id);
  return <HuntWordsScreen key={jogoId} jogoId={jogoId} />;
};

const HuntWordsScreen = ({ jogoId }: { jogoId: number }) => {
  const { user } = useAuth();
  const userId = user?.id;
  const { mountPopup } = usePopup();
  const navigate = useNavigate();

  const [loaded, setLoaded] = useState<WordSearchLoad | null>(null);
  const [progress, setProgress] = useState<WordSearchCache>(EMPTY);
  const [resetGame, setResetGame] = useState(false);
  const progressRef = useRef(progress);
  progressRef.current = progress;

  useEffect(() => {
    let alive = true;
    loadWordSearchGame(jogoId, userId)
      .then((res) => {
        if (!alive) return;
        if (!res) {
          message.warning('Este jogo ainda não tem conteúdo.');
          navigate('/');
          return;
        }
        setProgress({ found: res.found, correctCells: res.correctCells });
        setLoaded(res);
      })
      .catch(() => {
        if (!alive) return;
        message.error('Não foi possível abrir o jogo. Tente de novo.');
        navigate('/');
      });
    return () => {
      alive = false;
    };
  }, [jogoId, userId, navigate]);

  const all = useMemo(() => loaded?.solutions ?? [], [loaded]);
  const remaining = useMemo(() => remainingSolutions(all, progress.found), [all, progress.found]);
  const texto = useMemo(() => (loaded?.game.texto ?? '').split('*'), [loaded]);

  const session = useGameSession({
    ready: loaded !== null,
    savedSeconds: loaded?.savedSeconds ?? 0,
    alreadyFinished: loaded?.finished ?? null,
    onSaveProgress: (seconds) =>
      userId !== undefined
        ? saveWordSearchProgress({ userId, jogoId, cache: progressRef.current, finished: false, seconds })
        : undefined,
  });
  const { finish, isPlaying } = session;

  const handleAccept = useCallback((solution: ISolution, cells: number[]) => {
    setProgress((current) => addFound(current, solution, cells));
  }, []);

  // Fim automático: todas as palavras REAIS do jogo encontradas.
  useEffect(() => {
    if (!loaded || !isPlaying || !isWordSearchComplete(all, progress.found)) return;
    const final = progress;
    const memo: FinishMemo = {}; // o "Tentar de novo" reusa: manda só o que falhou
    void finish(wordSearchResult(all, final.found), async (seconds) => {
      if (userId === undefined) return;
      await finishWordSearch({ userId, jogoId, cache: final, seconds }, memo);
    });
  }, [all, finish, isPlaying, jogoId, loaded, progress, userId]);

  const askClear = () =>
    mountPopup({
      title: 'Limpar o jogo?',
      message: 'As palavras encontradas serão desmarcadas.',
      type: 'atencao',
      buttons: [
        {
          buttonText: 'Limpar',
          buttonFunction: () => {
            setProgress(EMPTY);
            setResetGame(true);
          },
        },
        { buttonText: 'Cancelar', isPurple: true },
      ],
    });

  const total = all.length;
  const remainingLabel = `Palavras restantes: ${remaining.length}/${total}`;
  // Enquanto carrega não há ações (nem "Ver resultado" sem resultado).
  const menuItems =
    session.status === 'loading'
      ? []
      : isPlaying
        ? [{ buttonText: remainingLabel }, { buttonText: 'Limpar jogo', buttonFunctions: askClear }]
        : [{ buttonText: remainingLabel }, { buttonText: 'Ver resultado', buttonFunctions: session.showResult }];
  const maxScore = wordsearchPoints(total);
  const currentScore = wordsearchPoints(progress.found.length);
  const puzzle = loaded?.game.game['crossword-compiler']['rectangular-puzzle']['word-search'];

  return (
    <Container>
      <GameHeader
        type="CACAPALAVRAS"
        maxScore={maxScore}
        currentScore={currentScore}
        seconds={session.seconds}
        menuItems={menuItems}
      />

      <Content>
        <GameContent>
          <div>
            {loaded && puzzle && (
              <Layout>
                <GameContainer>
                  <Game
                    i={parseInt(puzzle.grid.attributes.width, 10)}
                    j={parseInt(puzzle.grid.attributes.height, 10)}
                    data={puzzle}
                    solutions={remaining}
                    isFinished={!isPlaying}
                    resetGame={resetGame}
                    toggleResetGame={() => setResetGame(false)}
                    initialCorrectCells={loaded.correctCells}
                    acceptResponse={handleAccept}
                  />
                </GameContainer>
              </Layout>
            )}

            <Sidebar
              type="CACAPALAVRAS"
              maxScore={maxScore}
              currentScore={currentScore}
              seconds={session.seconds}
              menuItems={menuItems}
            />
          </div>
          <button type="button">{remainingLabel}</button>
          {loaded && (
            <Clue texto={texto} solutions={remaining} foundWordIds={progress.found.map((s) => s.wordId)} />
          )}
        </GameContent>
        <p style={{ background: '#9FA7F7' }}>
          VER MAIS COMENTÁRIOS <IoTriangleSharp size={12} />
        </p>
      </Content>

      <FinishModal
        visible={session.modalVisible}
        result={session.result}
        seconds={session.seconds}
        error={session.error}
        busy={session.status === 'finishing'}
        onRetry={session.retry}
        onClose={session.hideResult}
        onHome={() => navigate('/')}
      />
    </Container>
  );
};

export default HuntWords;
